/**
 * Static mock data for the Chancellery backoffice UI (SF-103), extracted from the interactive
 * Artifact mockup (docs/specs/sf103/design/sf103-chancellery-mockup.html). Consumed only through
 * hooks/useChancellery.ts — components must never import this module directly.
 */
import type { Universe, WorldDetail } from '../../types/chancellery';

const PRIME_WORLDS = [
  { worldId: 'emberreach', divRank: 1, name: 'Emberreach', kingdomCount: 142, boundaryAbove: null },
  { worldId: 'ashvale-reach', divRank: 2, name: 'Ashvale Reach', kingdomCount: 168, boundaryAbove: { swapPercent: 10 } },
  { worldId: 'drowned-court', divRank: 3, name: 'Drowned Court', kingdomCount: 210, boundaryAbove: { swapPercent: 12 } },
  { worldId: 'stormwatch', divRank: 4, name: 'Stormwatch', kingdomCount: 97, boundaryAbove: { swapPercent: 15 } },
];

const PLAYFUL_WORLDS = [
  { worldId: 'chaosreach', divRank: 1, name: 'Chaosreach', kingdomCount: 38, boundaryAbove: null },
  { worldId: 'tinkerhold', divRank: 2, name: 'Tinkerhold', kingdomCount: 52, boundaryAbove: { swapPercent: 15 } },
];

export const MOCK_UNIVERSES: Universe[] = [
  {
    universeId: 'prime',
    name: 'Prime',
    description: 'The main realm — every kingdom that matters plays here.',
    worlds: PRIME_WORLDS,
    currentAge: {
      ageNumber: 7,
      name: 'The Long Frost',
      status: 'ACTIVE',
      daysElapsed: 72,
      totalDays: 84,
      startedAtLabel: '12 May 2026',
      endsAtLabel: '4 Aug 2026',
      pausedDaysAgo: null,
    },
    nextAge: { ageNumber: 8, startsAtLabel: '11 Aug 2026' },
    sandbox: { sandboxId: 'the-hollow-mirror', name: 'The Hollow Mirror', testerCount: 6 },
  },
  {
    universeId: 'playful',
    name: 'Playful',
    description: "An experimental realm for wild rule combinations before they (maybe) reach Prime.",
    worlds: PLAYFUL_WORLDS,
    currentAge: {
      ageNumber: 3,
      name: 'Wildcard Trials',
      status: 'PAUSED',
      daysElapsed: 40,
      totalDays: 84,
      startedAtLabel: '14 Jun 2026',
      endsAtLabel: null,
      pausedDaysAgo: 8,
    },
    nextAge: null,
    sandbox: null,
  },
];

/** Keyed by `${universeId}:${worldId}`. Only Emberreach (Prime, Div 1) is wired in the mockup. */
export const MOCK_WORLD_DETAILS: Record<string, WorldDetail> = {
  'prime:emberreach': {
    worldId: 'emberreach',
    universeId: 'prime',
    universeName: 'Prime',
    divRank: 1,
    name: 'Emberreach',
    size: [
      { label: 'Islands', current: 3, max: 4, note: '75% provisioned' },
      { label: 'Kingdoms', current: 142, max: 200, note: '71% filled · 50 per island' },
      { label: 'Provinces / kingdom (avg)', current: 18, max: 25, note: '72% of max, on average' },
    ],
    ageHistory: [
      {
        ageNumber: 7,
        name: 'The Long Frost',
        isCurrent: true,
        isGenesis: false,
        dateRangeLabel: 'Started 12 May 2026 · ongoing',
        promotedIn: 16,
        relegatedOut: 14,
      },
      {
        ageNumber: 6,
        name: 'The Hollow Peace',
        isCurrent: false,
        isGenesis: false,
        dateRangeLabel: '16 Feb 2026 → 11 May 2026',
        promotedIn: 12,
        relegatedOut: 11,
      },
      {
        ageNumber: 5,
        name: 'The Crimson March',
        isCurrent: false,
        isGenesis: false,
        dateRangeLabel: '24 Nov 2025 → 15 Feb 2026',
        promotedIn: 18,
        relegatedOut: 15,
      },
      {
        ageNumber: 4,
        name: 'The Silver Drought',
        isCurrent: false,
        isGenesis: false,
        dateRangeLabel: '1 Sep 2025 → 23 Nov 2025',
        promotedIn: 9,
        relegatedOut: 12,
      },
      {
        ageNumber: 3,
        name: 'The Iron Tide',
        isCurrent: false,
        isGenesis: false,
        dateRangeLabel: '9 Jun 2025 → 31 Aug 2025',
        promotedIn: 14,
        relegatedOut: 10,
      },
      {
        ageNumber: 2,
        name: 'The Bronze Accord',
        isCurrent: false,
        isGenesis: false,
        dateRangeLabel: '17 Mar 2025 → 8 Jun 2025',
        promotedIn: 11,
        relegatedOut: 9,
      },
      {
        ageNumber: 1,
        name: 'The First Dawn',
        isCurrent: false,
        isGenesis: true,
        dateRangeLabel: '24 Dec 2024 → 16 Mar 2025',
        promotedIn: null,
        relegatedOut: null,
      },
    ],
  },
};
